import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Image as ImageIcon,
  Upload,
  Search,
  Filter,
  Trash2,
  Copy,
  Check,
  Eye,
  Download,
  Edit3,
  ExternalLink,
  Plus,
  Grid,
  List as ListIcon,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  ShoppingBag,
  FileText,
  Layout,
  Layers,
  X,
  RefreshCw,
  FolderOpen,
  Info
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useLanguage } from '../../context/LanguageContext';

export default function MediaManager({ onSelectMedia, isModalMode = false, onCloseModal }) {
  const { lang } = useLanguage();
  const {
    products = [],
    blogs = [],
    pages = [],
    cmsContent = {},
    settings = {},
    mediaLibrary = [],
    addMedia,
    updateMedia,
    deleteMedia,
    syncMediaFromStore
  } = useStore();

  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [usageFilter, setUsageFilter] = useState('all'); // 'all', 'used', 'unused'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Detail Modal State
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [detailForm, setDetailForm] = useState({
    title: '',
    caption: '',
    altText: '',
    description: ''
  });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [addUrlModalOpen, setAddUrlModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlTitleInput, setUrlTitleInput] = useState('');

  // Auto-scan all store contents (Products, Blogs, Pages, Banners) on mount
  useEffect(() => {
    if (syncMediaFromStore) {
      syncMediaFromStore();
    }
  }, []);

  // Compute live usage map for each image URL
  const usageMap = useMemo(() => {
    const map = new Map();

    const registerUsage = (url, type, name, id) => {
      if (!url || typeof url !== 'string') return;
      const cleanUrl = url.trim();
      if (!cleanUrl) return;

      if (!map.has(cleanUrl)) {
        map.set(cleanUrl, []);
      }
      const list = map.get(cleanUrl);
      if (!list.some(item => item.id === id && item.type === type)) {
        list.push({ type, name, id });
      }
    };

    // 1. Scan Products
    products.forEach(p => {
      if (p.image) registerUsage(p.image, 'product', p.nameUrdu || p.name || 'پروڈکٹ', p.id);
      if (Array.isArray(p.images)) {
        p.images.forEach(img => registerUsage(img, 'product', p.nameUrdu || p.name || 'پروڈکٹ', p.id));
      }
    });

    // 2. Scan Blogs
    blogs.forEach(b => {
      if (b.coverImage) registerUsage(b.coverImage, 'blog', b.titleUrdu || b.title || 'بلاگ مضمون', b.id);
      if (b.featuredImage) registerUsage(b.featuredImage, 'blog', b.titleUrdu || b.title || 'بلاگ مضمون', b.id);
      // Scan in-HTML images
      const html = b.contentHtml || b.content || '';
      const imgMatches = html.match(/src=["']([^"']+)["']/g);
      if (imgMatches) {
        imgMatches.forEach(m => {
          const src = m.replace(/src=["']/, '').replace(/["']$/, '');
          registerUsage(src, 'blog-content', b.titleUrdu || b.title || 'بلاگ کا اندرونی متن', b.id);
        });
      }
    });

    // 3. Scan Pages
    pages.forEach(pg => {
      if (pg.coverImage) registerUsage(pg.coverImage, 'page', pg.titleUrdu || pg.title || 'پیج', pg.id);
      const html = pg.contentHtml || pg.content || '';
      const imgMatches = html.match(/src=["']([^"']+)["']/g);
      if (imgMatches) {
        imgMatches.forEach(m => {
          const src = m.replace(/src=["']/, '').replace(/["']$/, '');
          registerUsage(src, 'page-content', pg.titleUrdu || pg.title || 'پیج کا متن', pg.id);
        });
      }
    });

    // 4. Scan CMS Banners & Settings
    if (settings.logoUrl) registerUsage(settings.logoUrl, 'settings', 'سٹور لوگو (Logo)', 'logo');
    if (settings.bankQrUrl) registerUsage(settings.bankQrUrl, 'settings', 'بینک ادائیگی کیو آر کوڈ (QR Code)', 'qr');
    if (cmsContent?.hero?.backgroundImage) registerUsage(cmsContent.hero.backgroundImage, 'cms', 'ہوم پیج بینر', 'hero');
    if (cmsContent?.hakimProfile?.image) registerUsage(cmsContent.hakimProfile.image, 'cms', 'حکیم صاحب کی تصویر', 'hakim');

    return map;
  }, [products, blogs, pages, cmsContent, settings]);

  // Merge mediaLibrary with any discovered usage
  const allMediaItems = useMemo(() => {
    const list = [...(mediaLibrary || [])];
    return list.map(item => {
      const usages = usageMap.get(item.url) || [];
      return {
        ...item,
        usages,
        isUsed: usages.length > 0
      };
    });
  }, [mediaLibrary, usageMap]);

  // Filtered & Sorted Media items
  const filteredMedia = useMemo(() => {
    return allMediaItems.filter(item => {
      const q = searchQuery.toLowerCase().trim();
      const matchSearch = !q || 
        (item.title || '').toLowerCase().includes(q) ||
        (item.altText || '').toLowerCase().includes(q) ||
        (item.caption || '').toLowerCase().includes(q) ||
        (item.url || '').toLowerCase().includes(q);

      const matchUsage = usageFilter === 'all' 
        ? true 
        : usageFilter === 'used' 
          ? item.isUsed 
          : !item.isUsed;

      return matchSearch && matchUsage;
    }).sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (sortBy === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      if (sortBy === 'name') return (a.title || '').localeCompare(b.title || '');
      return 0;
    });
  }, [allMediaItems, searchQuery, usageFilter, sortBy]);

  // Stats calculation
  const totalCount = allMediaItems.length;
  const usedCount = allMediaItems.filter(m => m.isUsed).length;
  const unusedCount = totalCount - usedCount;

  // Image compression & metadata helper
  const processImageFile = (file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        return reject(new Error('صرف تصویر فائل سپورٹڈ ہے'));
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width;
          let h = img.height;
          const maxWidth = 1400;
          if (w > maxWidth) {
            h = Math.round((h * maxWidth) / w);
            w = maxWidth;
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/webp', 0.88);

          // Estimate file size in KB
          const approxKb = Math.round((dataUrl.length * 3) / 4 / 1024);

          const fileName = file.name.replace(/\.[^/.]+$/, '');
          const formattedTitle = fileName.replace(/[-_]+/g, ' ');

          resolve({
            id: `media-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            url: dataUrl,
            title: formattedTitle || 'نئی اپلوڈ شدہ تصویر',
            caption: '',
            altText: formattedTitle || 'ہربل معلوماتی تصویر',
            description: '',
            dimensions: `${w} × ${h}`,
            fileSize: `${approxKb} KB`,
            fileType: 'image/webp',
            createdAt: new Date().toISOString().slice(0, 10)
          });
        };
        img.onerror = () => reject(new Error('تصویر لوڈ کرنے میں مسئلہ'));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error('فائل پڑھنے میں مسئلہ'));
      reader.readAsDataURL(file);
    });
  };

  // Handle file uploads
  const handleUploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          const mediaObj = await processImageFile(file);
          if (addMedia) {
            addMedia(mediaObj);
          }
        }
      }
    } catch (err) {
      alert('تصویر اپلوڈ کرنے میں مسئلہ: ' + err.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Add image from URL
  const handleAddFromUrl = (e) => {
    e?.preventDefault();
    if (!urlInput || !urlInput.startsWith('http')) {
      alert('براہ کرم درست تصویر کا URL درج کریں۔');
      return;
    }
    const mediaObj = {
      id: `media-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      url: urlInput.trim(),
      title: urlTitleInput.trim() || 'انٹرنیٹ تصویر',
      caption: '',
      altText: urlTitleInput.trim() || 'ہربل معلوماتی تصویر',
      description: '',
      dimensions: 'انٹرنیٹ سورس',
      fileSize: 'آن لائن لنک',
      fileType: 'image/url',
      createdAt: new Date().toISOString().slice(0, 10)
    };
    if (addMedia) addMedia(mediaObj);
    setUrlInput('');
    setUrlTitleInput('');
    setAddUrlModalOpen(false);
  };

  // Open Media Inspector Modal
  const handleOpenDetail = (media) => {
    setSelectedMedia(media);
    setDetailForm({
      title: media.title || '',
      caption: media.caption || '',
      altText: media.altText || '',
      description: media.description || ''
    });
  };

  // Save Media Metadata
  const handleSaveMeta = (e) => {
    e?.preventDefault();
    if (!selectedMedia) return;
    if (updateMedia) {
      updateMedia(selectedMedia.id, detailForm);
    }
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  // Delete Media
  const handleDeleteMedia = (mediaId) => {
    if (deleteMedia) {
      deleteMedia(mediaId);
    }
    setDeleteConfirmId(null);
    if (selectedMedia?.id === mediaId) {
      setSelectedMedia(null);
    }
  };

  // Copy Link
  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2500);
  };

  return (
    <div className="space-y-6" dir="rtl">
      
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleUploadFiles(e.target.files)}
        accept="image/*"
        multiple
        className="hidden"
      />

      {/* Top Header Card */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-gray-200/80 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 bg-emerald-50 text-emerald-700 rounded-xl">
              <ImageIcon className="w-5 h-5" />
            </span>
            <h2 className="text-xl sm:text-2xl font-black font-urdu text-gray-900">
              میڈیا لائبریری و فائلز مینیجر (WordPress Media Library)
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 font-urdu">
            ویب سائٹ کی تمام اپلوڈ شدہ تصاویر دیکھیں، کیپشن و آلٹ ٹیکسٹ (SEO) لگائیں، اور فالتو فائلز حذف کریں۔
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            type="button"
            onClick={() => setAddUrlModalOpen(true)}
            className="px-4 py-2.5 rounded-2xl bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-bold text-xs font-urdu shadow-2xs transition cursor-pointer flex items-center gap-1.5"
          >
            <ExternalLink className="w-4 h-4 text-emerald-700" />
            <span>URL سے شامل کریں</span>
          </button>

          <button
            type="button"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-pansar-700 hover:bg-pansar-800 disabled:opacity-50 text-white font-bold text-xs sm:text-sm font-urdu shadow-md hover:shadow-lg transition cursor-pointer"
          >
            <Upload className="w-4 h-4" />
            <span>{isUploading ? 'اپلوڈ ہو رہی ہیں...' : '+ نئی تصویر اپلوڈ کریں'}</span>
          </button>
        </div>
      </div>

      {/* Drag & Drop Upload Zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleUploadFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`p-6 sm:p-8 rounded-3xl border-2 border-dashed transition-all text-center cursor-pointer flex flex-col items-center justify-center gap-2 ${
          isDragging
            ? 'border-emerald-500 bg-emerald-50/80 scale-[0.99]'
            : 'border-gray-300 hover:border-emerald-400 bg-white hover:bg-emerald-50/20'
        }`}
      >
        <div className="w-12 h-12 rounded-2xl bg-emerald-100/70 text-emerald-800 flex items-center justify-center shadow-xs">
          <Upload className="w-6 h-6 animate-bounce" />
        </div>
        <div>
          <h3 className="text-sm sm:text-base font-bold text-gray-800 font-urdu">
            تصاویر یہاں ڈریگ کر کے چھوڑیں یا فائلز منتخب کرنے کے لیے کلک کریں
          </h3>
          <p className="text-xs text-gray-400 font-urdu mt-0.5">
            ایک ساتھ متعدد تصاویر (JPG, PNG, WebP) منتخب کی جا سکتی ہیں۔
          </p>
        </div>
      </div>

      {/* Quick Stats Badges */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-200/70 shadow-2xs">
          <span className="text-xs font-bold text-gray-500 font-urdu block">کل میڈیا فائلز</span>
          <span className="text-2xl font-black text-gray-900 font-sans">{totalCount}</span>
        </div>

        <div className="bg-emerald-50/70 p-4 rounded-2xl border border-emerald-100 shadow-2xs">
          <span className="text-xs font-bold text-emerald-800 font-urdu block">استعمال شدہ (Used In Store)</span>
          <span className="text-2xl font-black text-emerald-900 font-sans">{usedCount}</span>
        </div>

        <div className="bg-amber-50/70 p-4 rounded-2xl border border-amber-100 shadow-2xs">
          <span className="text-xs font-bold text-amber-800 font-urdu block">غیر استعمال شدہ (Unused / Free)</span>
          <span className="text-2xl font-black text-amber-900 font-sans">{unusedCount}</span>
        </div>

        <div className="bg-blue-50/70 p-4 rounded-2xl border border-blue-100 shadow-2xs">
          <span className="text-xs font-bold text-blue-800 font-urdu block">سٹوریج اور فارمیٹ</span>
          <span className="text-sm font-black text-blue-900 font-urdu block mt-1">خودکار ویب پی آپٹمائزڈ</span>
        </div>
      </div>

      {/* Filters & View Switcher Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute right-3 top-3.5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="تصویر کا عنوان، آلٹ ٹیکسٹ یا لنک تلاش کریں..."
            className="w-full pr-9 pl-3 py-2.5 rounded-xl border border-gray-200 text-xs sm:text-sm font-urdu focus:outline-none focus:border-pansar-600 bg-gray-50/50"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
          {/* Usage Filter */}
          <select
            value={usageFilter}
            onChange={(e) => setUsageFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-urdu bg-white text-gray-700 focus:outline-none cursor-pointer"
          >
            <option value="all">تمام میڈیا فائلز ({totalCount})</option>
            <option value="used">صرف استعمال شدہ ({usedCount})</option>
            <option value="unused">صرف غیر استعمال شدہ ({unusedCount})</option>
          </select>

          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-urdu bg-white text-gray-700 focus:outline-none cursor-pointer"
          >
            <option value="newest">تازہ ترین اپلوڈ پہلے</option>
            <option value="oldest">پرانی اپلوڈ پہلے</option>
            <option value="name">نام کے لحاظ سے (A-Z)</option>
          </select>

          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-xl p-0.5 border border-gray-200">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                viewMode === 'grid' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-500 hover:text-gray-900'
              }`}
              title="گرڈ ویو (Grid View)"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-lg transition cursor-pointer ${
                viewMode === 'list' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-500 hover:text-gray-900'
              }`}
              title="لسٹ ویو (List View)"
            >
              <ListIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Media Content Area */}
      {filteredMedia.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-gray-200/80 space-y-3">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto" />
          <h3 className="text-base font-bold text-gray-700 font-urdu">کوئی تصویر نہیں ملی</h3>
          <p className="text-xs text-gray-500 font-urdu">آپ اوپر دیے گئے بٹن سے نئی تصاویر اپلوڈ کر سکتے ہیں۔</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-pansar-700 text-white rounded-xl text-xs font-bold font-urdu cursor-pointer hover:bg-pansar-800 transition"
          >
            تصویر اپلوڈ کریں
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        /* GRID VIEW */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredMedia.map((media) => (
            <div
              key={media.id}
              onClick={() => {
                if (onSelectMedia) {
                  onSelectMedia(media);
                } else {
                  handleOpenDetail(media);
                }
              }}
              className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs hover:shadow-md transition-all overflow-hidden flex flex-col group cursor-pointer relative"
            >
              {/* Image Preview */}
              <div className="relative aspect-square bg-gray-100 overflow-hidden flex items-center justify-center">
                <img
                  src={media.url}
                  alt={media.altText || media.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&auto=format&fit=crop&q=80';
                  }}
                />

                {/* Usage Badge Overlay */}
                <div className="absolute top-2 right-2">
                  {media.isUsed ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-600/95 text-white text-[10px] font-bold font-urdu shadow-xs flex items-center gap-1">
                      <CheckCircle2 className="w-2.5 h-2.5" /> استعمال شدہ
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-gray-900/70 text-gray-200 text-[10px] font-urdu backdrop-blur-xs">
                      فری / غیر استعمال
                    </span>
                  )}
                </div>

                {/* Hover Quick Action Buttons */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 p-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDetail(media);
                    }}
                    className="p-2 bg-white text-gray-900 rounded-xl hover:bg-emerald-50 transition shadow-md"
                    title="تفصیلات دیکھیں"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyLink(media.url);
                    }}
                    className="p-2 bg-white text-emerald-800 rounded-xl hover:bg-emerald-50 transition shadow-md"
                    title="لنک کاپی کریں"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Title & Date */}
              <div className="p-2.5 text-right flex flex-col justify-between flex-1">
                <h4 className="text-xs font-bold text-gray-900 font-urdu truncate" title={media.title}>
                  {media.title || 'بلا عنوان'}
                </h4>
                <div className="flex items-center justify-between text-[10px] text-gray-400 font-sans mt-1">
                  <span>{media.fileSize || 'WebP'}</span>
                  <span>{media.createdAt}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* LIST VIEW */
        <div className="bg-white rounded-3xl border border-gray-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-right text-xs font-urdu">
              <thead className="bg-gray-50/80 text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="p-3.5 font-bold">تصویر</th>
                  <th className="p-3.5 font-bold">عنوان و نام</th>
                  <th className="p-3.5 font-bold">آلٹ ٹیکسٹ (SEO)</th>
                  <th className="p-3.5 font-bold">سائز / ریزولیوشن</th>
                  <th className="p-3.5 font-bold">استعمال کی جگہ</th>
                  <th className="p-3.5 font-bold">تاریخ</th>
                  <th className="p-3.5 font-bold text-center">ایکشنز</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {filteredMedia.map((media) => (
                  <tr key={media.id} className="hover:bg-gray-50/50 transition">
                    <td className="p-3">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 overflow-hidden border border-gray-200 shrink-0">
                        <img
                          src={media.url}
                          alt={media.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </td>
                    <td className="p-3 font-bold text-gray-900 max-w-[180px] truncate">
                      {media.title || 'بلا عنوان'}
                    </td>
                    <td className="p-3 text-gray-600 max-w-[160px] truncate">
                      {media.altText || '—'}
                    </td>
                    <td className="p-3 font-sans text-gray-500">
                      {media.dimensions || '1200x800'} ({media.fileSize || 'WebP'})
                    </td>
                    <td className="p-3">
                      {media.isUsed ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 font-bold text-[11px] border border-emerald-100">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          <span>{media.usages[0]?.name || 'سٹور میں استعمال'}</span>
                          {media.usages.length > 1 && (
                            <span className="text-[10px] text-emerald-600">+{media.usages.length - 1} دیگر</span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-block px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-[11px]">
                          غیر استعمال شدہ
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-sans text-gray-400 text-[11px]">
                      {media.createdAt}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenDetail(media)}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-emerald-50 text-gray-700 hover:text-emerald-800 transition cursor-pointer"
                          title="تفصیلات"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyLink(media.url)}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-800 transition cursor-pointer"
                          title="لنک کاپی کریں"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteMedia(media.id)}
                          className="p-1.5 rounded-lg bg-gray-100 hover:bg-rose-50 text-gray-700 hover:text-rose-600 transition cursor-pointer"
                          title="ڈیلیٹ کریں"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MEDIA DETAILS & INSPECTOR MODAL (WORDPRESS MEDIA ATTACHMENT DETAILS) */}
      {/* ========================================================================= */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6 backdrop-blur-xs animate-fade-in" dir="rtl">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto border border-gray-200 shadow-2xl flex flex-col">
            
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <span className="p-2 bg-emerald-50 text-emerald-800 rounded-xl">
                  <ImageIcon className="w-4 h-4" />
                </span>
                <h3 className="font-bold text-gray-900 font-urdu text-base sm:text-lg">
                  تصویر کی مکمل تفصیلات اور ایس ای او ترتیبات (Attachment Details)
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMedia(null)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body: 2 Columns (Image Preview on Left, Metadata Form on Right) */}
            <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-12 gap-6">
              
              {/* Left Column: Image Preview & File Meta */}
              <div className="md:col-span-6 space-y-4">
                <div className="rounded-2xl overflow-hidden bg-gray-950/5 border border-gray-200 flex items-center justify-center p-2 min-h-[260px] max-h-[380px]">
                  <img
                    src={selectedMedia.url}
                    alt={selectedMedia.altText || selectedMedia.title}
                    className="max-h-[360px] w-auto max-w-full object-contain rounded-xl shadow-md"
                  />
                </div>

                {/* File Information Pill Box */}
                <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200/80 text-xs font-urdu space-y-2">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>اپلوڈ تاریخ:</span>
                    <span className="font-sans font-bold text-gray-800">{selectedMedia.createdAt}</span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>حجم و سائز:</span>
                    <span className="font-sans font-bold text-gray-800">{selectedMedia.fileSize || 'WebP Compressed'}</span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>ڈائمینشنز (Dimensions):</span>
                    <span className="font-sans font-bold text-gray-800">{selectedMedia.dimensions || '1200 × 800 px'}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-gray-500">فائل لنک:</span>
                    <button
                      type="button"
                      onClick={() => handleCopyLink(selectedMedia.url)}
                      className="px-3 py-1 rounded-lg bg-white border border-gray-200 hover:bg-emerald-50 text-emerald-800 font-bold flex items-center gap-1 transition cursor-pointer shadow-2xs"
                    >
                      {copiedUrl ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedUrl ? 'کاپی ہو گیا!' : 'لنک کاپی کریں'}</span>
                    </button>
                  </div>
                </div>

                {/* Usage Locations List */}
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-100 text-xs font-urdu space-y-2">
                  <span className="font-bold text-emerald-950 block flex items-center gap-1">
                    <Info className="w-3.5 h-3.5 text-emerald-700" />
                    <span>ویب سائٹ میں کہاں استعمال ہو رہی ہے؟</span>
                  </span>
                  {selectedMedia.usages && selectedMedia.usages.length > 0 ? (
                    <ul className="space-y-1 mr-2 text-emerald-900">
                      {selectedMedia.usages.map((u, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          {u.type === 'product' && <ShoppingBag className="w-3 h-3 text-emerald-700 shrink-0" />}
                          {u.type.includes('blog') && <FileText className="w-3 h-3 text-emerald-700 shrink-0" />}
                          {u.type.includes('page') && <Layout className="w-3 h-3 text-emerald-700 shrink-0" />}
                          <span className="font-semibold">{u.name}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-[11px]">
                      یہ تصویر فی الحال کسی پراڈکٹ یا بلاگ میں منسلک نہیں ہے۔ آپ اسے نئے مضامین میں استعمال کر سکتے ہیں۔
                    </p>
                  )}
                </div>
              </div>

              {/* Right Column: Editable Metadata Form */}
              <div className="md:col-span-6 space-y-4">
                <form onSubmit={handleSaveMeta} className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      تصویر کا عنوان (Title) *
                    </label>
                    <input
                      type="text"
                      value={detailForm.title}
                      onChange={(e) => setDetailForm({ ...detailForm, title: e.target.value })}
                      placeholder="مثلاً: کلونجی کے بیج اور طبی فوائد"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-pansar-600"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      متبادل متن (Alt Text - گوگل SEO اور تصویری تلاش کے لیے لازمی)
                    </label>
                    <input
                      type="text"
                      value={detailForm.altText}
                      onChange={(e) => setDetailForm({ ...detailForm, altText: e.target.value })}
                      placeholder="تصویر کی وضاحت لکھیں تاکہ گوگل سرچ پر ریکنگ بہتر ہو"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-pansar-600"
                    />
                    <span className="text-[10px] text-gray-400 font-urdu mt-0.5 block">
                      گوگل اور نابینا قارئین کے لیے تصویر کی مختصر وضاحت
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      کیپشن (Caption - مضمون میں تصویر کے نیچے نظر آئے گا)
                    </label>
                    <textarea
                      rows={2}
                      value={detailForm.caption}
                      onChange={(e) => setDetailForm({ ...detailForm, caption: e.target.value })}
                      placeholder="تصویر کا مختصر وضاحتی جملہ..."
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-pansar-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                      تفصیلی وضاحت (Description - اندرونی ریکارڈ)
                    </label>
                    <textarea
                      rows={3}
                      value={detailForm.description}
                      onChange={(e) => setDetailForm({ ...detailForm, description: e.target.value })}
                      placeholder="تصویر کا سورس یا اضافی نوٹس..."
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-pansar-600"
                    />
                  </div>

                  {saveSuccess && (
                    <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800 text-xs font-bold font-urdu flex items-center gap-1.5 animate-pulse">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>تبدیلیاں کامیابی سے محفوظ ہو گئیں!</span>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <button
                      type="submit"
                      className="px-5 py-2.5 rounded-xl bg-pansar-700 hover:bg-pansar-800 text-white font-bold text-xs font-urdu shadow-sm transition cursor-pointer flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4" />
                      <span>معلومات محفوظ کریں</span>
                    </button>

                    {deleteConfirmId === selectedMedia.id ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleDeleteMedia(selectedMedia.id)}
                          className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold font-urdu cursor-pointer"
                        >
                          ہاں، ڈیلیٹ کریں
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-xl text-xs font-bold font-urdu cursor-pointer"
                        >
                          منسوخ
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(selectedMedia.id)}
                        className="px-3.5 py-2 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold font-urdu transition cursor-pointer flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>ڈیلیٹ کریں</span>
                      </button>
                    )}
                  </div>
                </form>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: ADD IMAGE FROM INTERNET URL */}
      {/* ========================================================================= */}
      {addUrlModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-xs" dir="rtl">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-gray-200 shadow-2xl space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 font-urdu text-base flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-emerald-700" />
                <span>انٹرنیٹ لنک (URL) سے تصویر شامل کریں</span>
              </h3>
              <button
                type="button"
                onClick={() => setAddUrlModalOpen(false)}
                className="text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddFromUrl} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  تصویر کا ویب لنک (Image URL) *
                </label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-sans dir-ltr text-left focus:outline-none focus:border-emerald-600"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  تصویر کا عنوان (Title)
                </label>
                <input
                  type="text"
                  value={urlTitleInput}
                  onChange={(e) => setUrlTitleInput(e.target.value)}
                  placeholder="مثلاً: کلونجی کے فوائد"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setAddUrlModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-urdu font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                >
                  منسوخ
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-urdu font-bold shadow-xs cursor-pointer"
                >
                  شامل کریں
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
